import {HeaderAgentTasks} from '@github-ui/issue-agent-tasks/HeaderAgentTasks'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {Suspense} from 'react'

import type {OptionConfig} from '../OptionConfig'
import type {HeaderBlockedBySummaryFragment} from './HeaderBlockedBySummary'
import {HeaderBlockedBySummary} from './HeaderBlockedBySummary'
import {SmallHeaderIssueType} from './HeaderIssueType'
import {HeaderSubIssueSummary, type HeaderSubIssueSummaryFragment} from './HeaderSubIssueSummary'
import type {LinkedSecurityAlertsFragment} from './LinkedSecurityAlerts'
import {LinkedSecurityAlerts} from './LinkedSecurityAlerts'
import {SmallRepositoryPill} from './RepositoryPill'
import type {
  StickyHeaderIssueNode,
  StickyHeaderIssueTypeNode,
  StickyHeaderRepositoryNode,
  StickyHeaderRepositoryOwnerNode,
} from './sticky-header-query-types'
import styles from './StickyHeaderChips.module.css'
import {SmallTrackedBy, type TrackedByFragment} from './TrackedBy'

// RELAY-CLEANUP(StickyHeader): widens nullable fields so the still-Relay parent data is assignable.
// Remove when StickyHeader leaves Relay.
export type StickyHeaderChipsFragment = RelayCompat<
  Pick<StickyHeaderIssueNode, 'url' | 'subIssuesSummary' | 'agentAssignments' | 'closedByPullRequestsReferences'> & {
    readonly repository: Pick<StickyHeaderRepositoryNode, 'nameWithOwner' | 'name' | 'isPrivate'> & {
      readonly owner: Pick<StickyHeaderRepositoryOwnerNode, 'login'>
    }
    readonly issueType: Pick<StickyHeaderIssueTypeNode, 'name' | 'color'> | null
  }
>

export type StickyHeaderChipsProps = {
  issueData: StickyHeaderChipsFragment
  /** Read from the header refetch, so the blocked-by summary follows the live state. */
  state?: HeaderBlockedBySummaryFragment['state']
  linkedSecurityAlertsData?: LinkedSecurityAlertsFragment
  blockedBySecondaryData?: HeaderBlockedBySummaryFragment
  trackedBySecondaryData?: TrackedByFragment
  subIssueSecondaryData?: HeaderSubIssueSummaryFragment
  optionConfig: OptionConfig
}

export function StickyHeaderChips({
  issueData: data,
  state,
  linkedSecurityAlertsData,
  blockedBySecondaryData,
  trackedBySecondaryData,
  subIssueSecondaryData,
  optionConfig,
}: StickyHeaderChipsProps) {
  const {showRepositoryPill} = optionConfig

  return (
    <div className={styles.chipsRow}>
      {/* RELAY-CLEANUP(StickyHeader): open-enum `color` from the still-Relay parent.
          Remove when StickyHeader leaves Relay. */}
      <SmallHeaderIssueType data={data} />
      {/* RELAY-CLEANUP(StickyHeader): open-enum `state` from the still-Relay parent.
          Remove when StickyHeader leaves Relay. */}
      <HeaderAgentTasks issueData={data} isSticky />
      <HeaderSubIssueSummary subIssuePrimaryKey={data} subIssueSecondaryKey={subIssueSecondaryData} size="small" />
      {linkedSecurityAlertsData && (
        <>
          <HeaderBlockedBySummary
            blockedBySecondaryData={blockedBySecondaryData}
            state={state}
            size="small"
            repositoryNameWithOwner={data.repository.nameWithOwner}
          />
          <LinkedSecurityAlerts issueKey={linkedSecurityAlertsData} isSmall />
          <Suspense>
            <SmallTrackedBy url={data.url} trackedByData={trackedBySecondaryData} />
          </Suspense>
        </>
      )}
      {showRepositoryPill && <SmallRepositoryPill repositoryPillData={data} />}
    </div>
  )
}
